import { useCallback, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../../lib/api';
import { Banner } from '../../components/Banner';
import { Button } from '../../components/Button';
import { EmptyState } from '../../components/EmptyState';
import { Input } from '../../components/Input';
import { Modal } from '../../components/Modal';
import { errorMessage, formatDate, useLoad } from '../members/hooks';
import { OrgStatusChip } from './OrgsPage';
import { previousMonths, type Org, type OrgUsage } from './types';

const MONTHS = 6;

export function OrgDetailPage() {
  const { orgId = '' } = useParams();
  const loader = useCallback(async () => {
    const [orgs, ...usage] = await Promise.all([
      api.get<{ items: Org[] }>('/v1/orgs'),
      ...previousMonths(MONTHS).map((m) => api.get<OrgUsage>(`/v1/operator/orgs/usage?month=${m}`)),
    ]);
    const org = orgs.items.find((o) => o.id === orgId) ?? null;
    const months = usage.map((u) => ({ month: u.month, art: u.items.find((i) => i.orgId === orgId)?.qrArt ?? { total: 0, fromPool: 0, generated: 0 } }));
    return { org, months };
  }, [orgId]);
  const { data, error, loading, reload } = useLoad(loader);

  const [blockOpen, setBlockOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);
  const closeBlock = useCallback(() => {
    setBlockOpen(false);
    setReason('');
  }, []);

  async function setStatus(action: 'block' | 'unblock') {
    setBusy(true);
    setNotice(null);
    try {
      await api.post(`/v1/orgs/${encodeURIComponent(orgId)}/${action}`, action === 'block' ? { reason: reason.trim() } : undefined);
      setNotice({
        tone: 'success',
        text: action === 'block'
          ? 'Bloqueamos la organización: sus miembros y API keys pierden acceso. Los QR activados siguen redirigiendo.'
          : 'Desbloqueamos la organización.',
      });
      closeBlock();
      await reload();
    } catch (err) {
      setNotice({ tone: 'error', text: errorMessage(err) });
    } finally {
      setBusy(false);
    }
  }

  if (!loading && data && !data.org) {
    return <EmptyState title="Organización no encontrada" action={<Link to="/operador/organizaciones">Volver a organizaciones</Link>} />;
  }

  const org = data?.org;
  const max = Math.max(1, ...(data?.months.map((m) => m.art.total) ?? [1]));

  return (
    <section className="space-y-5">
      <Link to="/operador/organizaciones" className="text-sm">← Organizaciones</Link>
      {error && <Banner tone="error">{error}</Banner>}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-xl font-bold text-ink">{org?.name ?? orgId}</h1>
          <p className="text-sm text-ink-muted">
            <span className="font-mono">{org?.slug ?? orgId}</span> · {org?.type ?? '—'} · creada el {formatDate(org?.createdAt)}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {org && <OrgStatusChip status={org.status} />}
          {org?.status === 'blocked' ? (
            <Button variant="secondary" loading={busy} onClick={() => void setStatus('unblock')}>Desbloquear</Button>
          ) : (
            <Button variant="danger" disabled={!org} onClick={() => setBlockOpen(true)}>Bloquear organización…</Button>
          )}
        </div>
      </div>
      <div aria-live="polite">{notice && <Banner tone={notice.tone}>{notice.text}</Banner>}</div>

      <div className="rounded-lg border border-line bg-white p-4">
        <h2 className="mb-3 font-display text-base font-bold text-ink">QR Art por mes</h2>
        {loading && !data ? (
          <div className="h-32 animate-pulse rounded bg-gray-100" />
        ) : (
          <table className="w-full text-sm">
            <caption className="sr-only">Consumo de QR Art de los últimos {MONTHS} meses</caption>
            <thead className="text-xs text-ink-muted">
              <tr>
                <th scope="col" className="py-1 text-left font-medium">Mes</th>
                <th scope="col" className="py-1 text-right font-medium">Pool</th>
                <th scope="col" className="py-1 text-right font-medium">Generado</th>
                <th scope="col" className="py-1 text-right font-medium">Total</th>
                <th scope="col" className="w-1/3 py-1"><span className="sr-only">Proporción</span></th>
              </tr>
            </thead>
            <tbody>
              {data?.months.map((m) => (
                <tr key={m.month} className="border-t border-line">
                  <td className="py-1.5 font-mono">{m.month}</td>
                  <td className="py-1.5 text-right tabular-nums">{m.art.fromPool}</td>
                  <td className="py-1.5 text-right tabular-nums">{m.art.generated}</td>
                  <td className="py-1.5 text-right font-semibold tabular-nums">{m.art.total}</td>
                  <td className="py-1.5 pl-4">
                    <div className="flex h-2 overflow-hidden rounded-full bg-mist" aria-hidden="true">
                      <div className="bg-green" style={{ width: `${(m.art.fromPool / max) * 100}%` }} />
                      <div className="bg-yellow" style={{ width: `${(m.art.generated / max) * 100}%` }} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <p className="mt-2 flex gap-4 text-xs text-ink-muted">
          <span className="flex items-center gap-1"><span className="h-2 w-3 rounded-full bg-green" aria-hidden="true" /> Pool</span>
          <span className="flex items-center gap-1"><span className="h-2 w-3 rounded-full bg-yellow" aria-hidden="true" /> Generado</span>
        </p>
      </div>

      <Modal
        open={blockOpen}
        onClose={closeBlock}
        size="sm"
        title={`Bloquear ${org?.name ?? orgId}`}
        footer={
          <>
            <Button variant="secondary" onClick={closeBlock}>Cancelar</Button>
            <Button variant="danger" loading={busy} disabled={reason.trim().length < 3} onClick={() => void setStatus('block')}>Bloquear</Button>
          </>
        }
      >
        <p className="text-sm">Miembros y API keys pierden acceso. Los QR activados de sus clientes siguen redirigiendo.</p>
        <Input label="Motivo" value={reason} maxLength={500} onChange={(e) => setReason(e.target.value)} />
      </Modal>
    </section>
  );
}
